import { api } from '@/lib/axios';
import type { ExportRequest } from '@/lib/export-request';
import type { DashboardAntwort } from '@symphenia/shared-types';
import type { Monatseingang, MonatsErgebnis, ZettelAnpassung, ZettelFelder } from '@symphenia/payroll-core';
import type {
  CollectiveAgreement,
  DayEntry,
  GenerateHolidaysResult,
  Holiday,
  HolidayInput,
  PaginationMeta,
  PayrollAbsenceType,
  PayrollAccess,
  PayrollAccessPerson,
  PayrollEmployee,
  PayrollAbsenceTypeInput,
  PayrollSetting,
  PayrollSettingInput,
  ResolvedSettings,
  WorkSchedule,
  WorkScheduleInput,
} from '../types/payroll.types';

interface Paged<T> {
  data: T[];
  meta: PaginationMeta;
}

// ── Kollektivverträge ────────────────────────────────────────────────────────

export async function fetchCollectiveAgreements(): Promise<CollectiveAgreement[]> {
  const res = await api.get<Paged<CollectiveAgreement>>('/payroll/collective-agreements');
  return res.data.data;
}

export async function createCollectiveAgreement(input: {
  name: string;
  kind?: 'COLLECTIVE' | 'FLAT';
}): Promise<CollectiveAgreement> {
  const res = await api.post<{ data: CollectiveAgreement }>('/payroll/collective-agreements', input);
  return res.data.data;
}

// ── KV-Vorlage des Betriebs (AP-22 Teil 6, K1) ──────────────────────────────

/** Was der Betrieb gewählt hat (`null` = nichts) und welche Vorlage damit gilt. */
export interface KvVorlageStand {
  templateKey: string | null;
  effective: string;
}

export async function fetchKvTemplate(): Promise<KvVorlageStand> {
  const res = await api.get<{ data: KvVorlageStand }>('/payroll/kv-template');
  return res.data.data;
}

export async function setKvTemplate(templateKey: string | null): Promise<KvVorlageStand> {
  const res = await api.put<{ data: KvVorlageStand }>('/payroll/kv-template', { templateKey });
  return res.data.data;
}

// ── Feiertage ────────────────────────────────────────────────────────────────
// Bearbeitet wird nicht hier, sondern feldweise über die Speicherstelle der
// Zeile (`lib/speichern`, AP-36) — mit Stand-Prüfung gegen Überschreiben.

export async function fetchHolidays(params: {
  year?: number;
  page?: number;
  limit?: number;
}): Promise<Paged<Holiday>> {
  const res = await api.get<Paged<Holiday>>('/payroll/holidays', { params });
  return res.data;
}

export async function createHoliday(input: HolidayInput): Promise<Holiday> {
  const res = await api.post<{ data: Holiday }>('/payroll/holidays', input);
  return res.data.data;
}

export async function deleteHoliday(id: string): Promise<void> {
  await api.delete(`/payroll/holidays/${id}`);
}

/**
 * Belegt ein Jahr aus der Berechnung vor. Bereits vorhandene Tage bleiben
 * unangetastet — das Backend überspringt sie und meldet, wie viele.
 */
export async function generateHolidays(year: number): Promise<GenerateHolidaysResult> {
  const res = await api.post<{ data: GenerateHolidaysResult }>('/payroll/holidays/generate', {
    year,
  });
  return res.data.data;
}

// ── Einstellungen ────────────────────────────────────────────────────────────

export async function fetchSettings(params: {
  key?: string;
  collectiveAgreementId?: string;
  page?: number;
  limit?: number;
}): Promise<Paged<PayrollSetting>> {
  const res = await api.get<Paged<PayrollSetting>>('/payroll/settings', { params });
  return res.data;
}

/** Neuer Satz (AP-36 Fall 2). Korrigiert wird über das Feld in der Zeile (PATCH). */
export async function createSetting(input: PayrollSettingInput): Promise<PayrollSetting> {
  const res = await api.post<{ data: PayrollSetting }>('/payroll/settings', input);
  return res.data.data;
}

export async function deleteSetting(id: string): Promise<void> {
  await api.delete(`/payroll/settings/${id}`);
}

/** Die für einen Stichtag gültigen Werte — die Eingabe des Rechenkerns. */
export async function resolveSettings(
  date: string,
  collectiveAgreementId?: string,
): Promise<ResolvedSettings> {
  const res = await api.get<{ data: ResolvedSettings }>('/payroll/settings/resolve', {
    params: { date, collectiveAgreementId },
  });
  return res.data.data;
}

// ── Arbeitszeitmodelle ───────────────────────────────────────────────────────

export async function fetchWorkSchedules(params: {
  userId?: string;
  page?: number;
  limit?: number;
}): Promise<Paged<WorkSchedule>> {
  const res = await api.get<Paged<WorkSchedule>>('/payroll/work-schedules', { params });
  return res.data;
}

export async function fetchWorkSchedule(id: string): Promise<WorkSchedule> {
  const res = await api.get<{ data: WorkSchedule }>(`/payroll/work-schedules/${id}`);
  return res.data.data;
}

/**
 * Export-Anfrage für den `ExportButton` (AP-10 Nachtrag 5): Download und
 * Mailversand entstehen daraus mit denselben Parametern — die Datei baut der
 * Server, nie der Browser.
 */
export function workSchedulesExportRequest(
  params: { userId?: string; format: string; orientation?: string; filterInfo?: string },
  columns: { key: string; label: string }[],
): ExportRequest {
  return {
    url: '/payroll/work-schedules/export',
    params: { ...params, columns: JSON.stringify(columns) },
  };
}

/**
 * Export der Monatsauswertung (AP-22-E). Der Zeitraum sind ganze Monate, die
 * Mitarbeiter kommen als Liste; ohne Liste nimmt der Server alle.
 */
export function monthsExportRequest(params: {
  from: string;
  to?: string;
  userIds?: string[];
  includeInactive?: boolean;
  variante?: 'ein-file' | 'datei-je-ma' | 'datei-je-monat';
  format: string;
}): ExportRequest {
  const { userIds, ...rest } = params;
  return {
    url: '/payroll/months/export',
    params: { ...rest, userIds: userIds && userIds.length > 0 ? userIds.join(',') : undefined },
  };
}

export async function createWorkSchedule(input: WorkScheduleInput): Promise<WorkSchedule> {
  const res = await api.post<{ data: WorkSchedule }>('/payroll/work-schedules', input);
  return res.data.data;
}

export async function deleteWorkSchedule(id: string): Promise<void> {
  await api.delete(`/payroll/work-schedules/${id}`);
}

// ── Fehlzeit-Arten (AP-22-D) ────────────────────────────────────────────────
// Bearbeitet wird feldweise über die Speicherstelle der Zeile (AP-36).

export async function fetchPayrollAbsenceTypes(): Promise<PayrollAbsenceType[]> {
  const res = await api.get<Paged<PayrollAbsenceType>>('/payroll/absence-types');
  return res.data.data;
}

export async function createPayrollAbsenceType(input: PayrollAbsenceTypeInput): Promise<PayrollAbsenceType> {
  const res = await api.post<{ data: PayrollAbsenceType }>('/payroll/absence-types', input);
  return res.data.data;
}

export async function deletePayrollAbsenceType(id: string): Promise<void> {
  await api.delete(`/payroll/absence-types/${id}`);
}

/** Übernimmt die Arten der Zeiterfassung, die noch fehlen. */
export async function importPayrollAbsenceTypes(): Promise<{ created: number; skipped: number }> {
  const res = await api.post<{ data: { created: number; skipped: number } }>('/payroll/absence-types/import');
  return res.data.data;
}

// ── Monatserfassung (AP-22-D) ───────────────────────────────────────────────

/** Was der Server zum Monat liefert — der Eingang der Engine plus Anzeigeangaben. */
export interface PayrollMonth extends Monatseingang {
  userId: string;
  zeilen: DayEntry[];
  modell:
    | (NonNullable<Monatseingang['modell']> & {
        id: string;
        validFrom: string;
        entryDate: string | null;
        exitDate: string | null;
      })
    | null;
  /** Beginnt im Monat ein weiteres Modell, dessen Gültig-ab — gerechnet wird mit dem ersten. */
  modellWechselAm: string | null;
  /** Die KV-Vorlage unter den Werten (Teil 6); fehlt bei Monaten, die davor eingefroren wurden. */
  vorlage?: string;
  arten: (Monatseingang['arten'][number] & { name: string; isActive: boolean })[];
  /** Stand des Lohnmonats (AP-22 Teil 2, G1). */
  abschluss: {
    status: 'OPEN' | 'FINAL';
    finalizedAt: string | null;
    finalizedBy: string | null;
    coreVersion: string | null;
    /** Zuletzt nach dem Fertigstellen geändert (G2). */
    changedAfterFinalAt: string | null;
    /** Einstellung des Mandanten: was mit einem fertiggestellten Monat geschehen darf. */
    bearbeitung: AbschlussBearbeitung;
    /** Wie der Monat befüllt wird (H): aus der Zeiterfassung, vom Abgabezettel, händisch. */
    weg: LohnmonatWeg;
    /** Weg A (D2): wann aus der Zeiterfassung übernommen wurde und was dabei offen blieb. */
    uebernahme: { importedAt: string | null; hinweise: { code: string; dates?: string[] }[] };
  };
  /** Nur bei einem fertiggestellten Lohnmonat: das eingefrorene Ergebnis. Die Maske rechnet dann nicht selbst. */
  ergebnis: MonatsErgebnis | null;
}

/** A aus der Zeiterfassung · B vom Abgabezettel · C händisch (AP-22 Teil 2, H). */
export type LohnmonatWeg = 'TIMESHEET' | 'SLIP' | 'MANUAL';

/** Ein hinterlegter Abgabezettel. */
export interface MonatsDatei {
  id: string;
  kind: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  /** I: wann der Zettel zuletzt gelesen wurde — dann gibt es einen Vorschlag. */
  readAt: string | null;
}

export async function setFillMode(userId: string, jahr: number, monat: number, mode: 'SLIP' | 'MANUAL'): Promise<LohnmonatWeg> {
  const res = await api.put<{ data: { weg: LohnmonatWeg } }>(`/payroll/months/${userId}/${jahr}/${monat}/fill-mode`, { mode });
  return res.data.data.weg;
}

export async function fetchMonthFiles(userId: string, jahr: number, monat: number): Promise<MonatsDatei[]> {
  const res = await api.get<{ data: MonatsDatei[] }>(`/payroll/months/${userId}/${jahr}/${monat}/files`);
  return res.data.data;
}

export async function uploadMonthFile(
  userId: string,
  jahr: number,
  monat: number,
  datei: File,
  change?: string,
): Promise<MonatsDatei> {
  const form = new FormData();
  form.append('file', datei);
  const res = await api.post<{ data: MonatsDatei }>(`/payroll/months/${userId}/${jahr}/${monat}/files`, form, {
    params: { change },
  });
  return res.data.data;
}

export async function deleteMonthFile(userId: string, jahr: number, monat: number, id: string, change?: string): Promise<void> {
  await api.delete(`/payroll/months/${userId}/${jahr}/${monat}/files/${id}`, { params: { change } });
}

/** Die Bytes über das Backend — mit Anmeldung, nie über einen offenen Link. */
export async function fetchMonthFileBlob(userId: string, jahr: number, monat: number, id: string): Promise<Blob> {
  const res = await api.get<Blob>(`/payroll/months/${userId}/${jahr}/${monat}/files/${id}/content`, {
    responseType: 'blob',
  });
  return res.data;
}

/** Der zuletzt hinterlegte Zettel je Mitarbeiter. */
export async function fetchLatestSlips(): Promise<{ userId: string; jahr: number; monat: number; fileId: string }[]> {
  const res = await api.get<{ data: { userId: string; jahr: number; monat: number; fileId: string }[] }>(
    '/payroll/slips/latest',
  );
  return res.data.data;
}

/** gesperrt · nur mit Grund · frei (AP-22 Teil 2, G2). */
export type AbschlussBearbeitung = 'LOCKED' | 'REASON' | 'FREE';

/** Ein Änderungsvorgang am fertiggestellten Lohnmonat samt dem, was er geändert hat. */
export interface MonatsAenderung {
  id: string;
  reason: string | null;
  startedAt: string;
  by: string;
  entries: { date: string; field: string; from: unknown; to: unknown; at: string }[];
}

export async function fetchClosingSetting(): Promise<AbschlussBearbeitung> {
  const res = await api.get<{ data: { mode: AbschlussBearbeitung } }>('/payroll/closing-setting');
  return res.data.data.mode;
}

export async function setClosingSetting(mode: AbschlussBearbeitung): Promise<AbschlussBearbeitung> {
  const res = await api.put<{ data: { mode: AbschlussBearbeitung } }>('/payroll/closing-setting', { mode });
  return res.data.data.mode;
}

export async function startMonthChange(
  userId: string,
  jahr: number,
  monat: number,
  reason?: string,
): Promise<{ id: string; reason: string | null }> {
  const res = await api.post<{ data: { id: string; reason: string | null } }>(
    `/payroll/months/${userId}/${jahr}/${monat}/changes`,
    { reason },
  );
  return res.data.data;
}

export async function fetchMonthChanges(userId: string, jahr: number, monat: number): Promise<MonatsAenderung[]> {
  const res = await api.get<{ data: MonatsAenderung[] }>(`/payroll/months/${userId}/${jahr}/${monat}/changes`);
  return res.data.data;
}

/** Ein Befund der Prüfung vor dem Fertigstellen. */
export interface MonatsBefund {
  code:
    | 'ALREADY_FINAL'
    | 'NO_WORK_SCHEDULE'
    | 'EMPTY'
    | 'DAYS_WITHOUT_ENTRY'
    | 'INCOMPLETE_TIMES'
    | 'SCHEDULE_CHANGE';
  dates?: string[];
}

export interface MonatsPruefung {
  verhindert: MonatsBefund[];
  hinweise: MonatsBefund[];
}

/** Stand eines Lohnmonats in der Übersicht — genannt, sobald er Tageszeilen hat oder fertiggestellt ist. */
export interface LohnmonatStand {
  userId: string;
  name: string;
  jahr: number;
  monat: number;
  tage: number;
  status: 'OPEN' | 'FINAL';
  finalizedAt: string | null;
}

export async function checkPayrollMonth(userId: string, jahr: number, monat: number): Promise<MonatsPruefung> {
  const res = await api.get<{ data: MonatsPruefung }>(`/payroll/months/${userId}/${jahr}/${monat}/check`);
  return res.data.data;
}

export async function finalizePayrollMonth(userId: string, jahr: number, monat: number): Promise<PayrollMonth> {
  const res = await api.post<{ data: PayrollMonth }>(`/payroll/months/${userId}/${jahr}/${monat}/finalize`);
  return res.data.data;
}

export async function fetchPayrollMonthStates(params: {
  from: string;
  to?: string;
  userIds?: string[];
  includeInactive?: boolean;
}): Promise<LohnmonatStand[]> {
  const { userIds, ...rest } = params;
  const res = await api.get<{ data: LohnmonatStand[] }>('/payroll/months/status', {
    params: { ...rest, userIds: userIds && userIds.length > 0 ? userIds.join(',') : undefined },
  });
  return res.data.data;
}

export async function fetchPayrollMonth(userId: string, jahr: number, monat: number): Promise<PayrollMonth> {
  const res = await api.get<{ data: PayrollMonth }>(`/payroll/months/${userId}/${jahr}/${monat}`);
  return res.data.data;
}

// ── Mitarbeiter und Zugriff (AP-38-D) ────────────────────────────────────────

/**
 * Die Mitarbeiter der Lohnverrechnung — eigene Liste statt `GET /users`, das
 * `core.user.read` verlangt und einer externen Lohnverrechnung die ganze
 * Benutzerverwaltung zeigte. Externe Personen stehen nicht darin.
 */
export async function fetchPayrollEmployees(params: { isActive?: boolean } = {}): Promise<PayrollEmployee[]> {
  const res = await api.get<Paged<PayrollEmployee>>('/payroll/employees', { params: { limit: 1000, ...params } });
  return res.data.data;
}

/** AP-38-F: Mitarbeiter ohne Zugang anlegen — die Adresse ist freiwillig. */
export async function createPayrollEmployee(input: {
  firstName: string;
  lastName: string;
  email: string | null;
}): Promise<PayrollEmployee> {
  const res = await api.post<{ data: PayrollEmployee }>('/payroll/employees', input);
  return res.data.data;
}

export async function fetchPayrollAccess(): Promise<PayrollAccess> {
  const res = await api.get<{ data: PayrollAccess }>('/payroll/access');
  return res.data.data;
}

/** Der Schalter „Lohnverrechnung" — Rolle und Platz in einem Zug. */
export async function setPayrollAccess(userId: string, lohnverrechnung: boolean): Promise<PayrollAccessPerson | null> {
  const res = await api.put<{ data: PayrollAccessPerson | null }>(`/payroll/access/${userId}`, { lohnverrechnung });
  return res.data.data;
}

export async function inviteExternalPayroll(input: {
  email: string;
  firstName?: string;
  lastName?: string;
}): Promise<void> {
  await api.post('/payroll/access/external', input);
}

export async function reinviteExternalPayroll(userId: string): Promise<void> {
  await api.post(`/payroll/access/external/${userId}/reinvite`);
}

export async function revokeExternalPayroll(userId: string): Promise<void> {
  await api.delete(`/payroll/access/external/${userId}`);
}

// ── Monatsübersicht und Dashboard (AP-22 Teil 2, J) ─────────────────────────

/** leer · in Arbeit · vollständig · fertiggestellt — gerechnet, nicht gespeichert. */
export type LohnmonatLage = 'EMPTY' | 'IN_PROGRESS' | 'COMPLETE' | 'FINAL';

/** Ein Mitarbeiter im Monat, wie ihn die Übersicht zeigt. */
export interface UebersichtZeile {
  userId: string;
  name: string;
  weg: LohnmonatWeg;
  lage: LohnmonatLage;
  tageErfasst: number;
  tageSoll: number;
  finalizedAt: string | null;
  changedAfterFinalAt: string | null;
  /** F: zuletzt in eine BMD-Datei aufgenommen. */
  exportedAt: string | null;
  zettel: number;
  hinweise: string[];
  normalStunden: number;
  mehrarbeit: number;
  ueberstunden: number;
  zaSaldo: number;
  fehlzeitTage: number;
  /** Teil 4: Nichtleistungszeit des Monats in Stunden. */
  nlz: number;
  /** Teil 3: Name der Entlohnungsart des Monats; null = Standard des Betriebs. */
  entlohnung: string | null;
}

/** Die Zeilen des Monats und wie viele aktive Mitarbeiter ohne Lohnverrechnung fehlen. */
export async function fetchMonthsOverview(month: string): Promise<{ zeilen: UebersichtZeile[]; ausgeblendet: number }> {
  const res = await api.get<{ data: UebersichtZeile[]; meta?: { ausgeblendet?: number } }>('/payroll/months/overview', {
    params: { month },
  });
  return { zeilen: res.data.data, ausgeblendet: res.data.meta?.ausgeblendet ?? 0 };
}

export async function finalizeAllMonths(
  month: string,
  userIds?: string[],
): Promise<{ fertiggestellt: number; ergebnisse: { userId: string; name: string; ok: boolean; code?: string }[] }> {
  const res = await api.post('/payroll/months/finalize-all', { month, userIds });
  return res.data.data;
}

/** Weg A, Nachholen: abgegebene Monatsberichte übernehmen, die noch nicht übernommen sind. */
export async function importSubmittedMonths(month?: string): Promise<{ uebernommen: number }> {
  const res = await api.post<{ data: { uebernommen: number } }>('/payroll/months/import', { month });
  return res.data.data;
}

/** Export der Monatsübersicht — die Datei entsteht am Server, auch für die Mail. */
export function monthsOverviewExportRequest(
  params: { month: string; format: string; orientation?: string; lage?: string; filterInfo?: string },
  columns: { key: string; label: string }[],
): ExportRequest {
  return { url: '/payroll/months/overview/export', params: { ...params, columns: JSON.stringify(columns) } };
}

/** Summen über die fertiggestellten Lohnmonate eines Monats. */
export interface Monatszahlen {
  lohnmonate: number;
  normalStunden: number;
  mehrarbeit: number;
  ueberstunden: number;
  ueberstundenGewichtet: number;
  zaSaldo: number;
  fehlzeitTage: Record<string, number>;
  naechtigungen: number;
  montagezeit: number;
  entfernungszulageBetrag: number;
  /** Summe der Beträge aller Zulagen des KV-Katalogs (Teil 6); fehlt bei einem älteren Server. */
  zulagenBetrag?: number;
}

export interface LohnDashboard extends DashboardAntwort {
  month: string;
  zahlen: { aktuell: Monatszahlen; vormonat: Monatszahlen };
}

export async function fetchPayrollDashboard(month?: string): Promise<LohnDashboard> {
  const res = await api.get<{ data: LohnDashboard }>('/payroll/dashboard', { params: { month } });
  return res.data.data;
}

// ── BMD-Export (AP-22 Teil 2, F) ────────────────────────────────────────────

export type BmdEinheit = 'HOURS' | 'DAYS' | 'COUNT' | 'AMOUNT';

/** Eine Größe der Monatsauswertung mit ihrer Lohnart — auch ohne Zuordnung eine Zeile. */
export interface BmdZuordnung {
  quantity: string;
  label: string;
  hinweis: string | null;
  einheiten: BmdEinheit[];
  wageType: string;
  unit: BmdEinheit;
  nlzCode: string;
  active: boolean;
  updatedAt: string | null;
  /** Teil 3: Was „für alle" gilt, wenn die Zeile zu einer Entlohnungsart gehört und nichts überschreibt. */
  inherited: { wageType: string; unit: BmdEinheit; nlzCode: string } | null;
}

export type BmdSpaltenArt =
  | 'KLIENT'
  | 'PERSONALNR'
  | 'NAME'
  | 'JAHR'
  | 'MONAT'
  | 'PERIODE'
  | 'LOHNART'
  | 'MENGE'
  | 'EINHEIT'
  | 'NLZ'
  | 'TEXT'
  | 'FEST';

export interface BmdSpalte {
  art: BmdSpaltenArt;
  titel?: string;
  wert?: string;
}

export interface BmdProfil {
  klientennummer: string;
  trennzeichen: ';' | ',' | 'TAB' | '|';
  dezimal: ',' | '.';
  kopfzeile: boolean;
  zeichensatz: 'windows-1252' | 'utf-8';
  dateiname: string;
  spalten: BmdSpalte[];
  updatedAt: string | null;
}

export interface BmdWarnung {
  code: 'NO_PERSONNEL_NUMBER' | 'UNMAPPED' | 'NOT_FINAL' | 'NO_CLIENT_NUMBER';
  namen?: string[];
}

export interface BmdVorschau {
  dateiname: string;
  mitarbeiter: number;
  zeilen: number;
  zuordnungen: number;
  vorschau: string;
  warnungen: BmdWarnung[];
}

/** Eine erzeugte BMD-Datei. */
export interface BmdUebergabe {
  id: string;
  year: number;
  month: number;
  createdAt: string;
  createdBy: string;
  employeeCount: number;
  lineCount: number;
  filename: string;
}

export type BmdUmfang = 'ALL' | 'NEW';

/** Ohne Entlohnungsart die Zuordnung „für alle"; mit einer deren Überschreibungen (AP-22 Teil 3). */
export async function fetchBmdWageTypes(agreement?: string): Promise<BmdZuordnung[]> {
  const res = await api.get<{ data: BmdZuordnung[] }>('/payroll/bmd/wage-types', { params: { agreement: agreement || undefined } });
  return res.data.data;
}

export async function fetchBmdProfile(): Promise<BmdProfil> {
  const res = await api.get<{ data: BmdProfil }>('/payroll/bmd/profile');
  return res.data.data;
}

/** Personalnummer und „keine Lohnverrechnung" je Mitarbeiter — nur, wo schon etwas gesetzt ist. */
export async function fetchPersonnelNumbers(): Promise<
  { userId: string; personnelNumber: string | null; noPayroll: boolean; updatedAt: string }[]
> {
  const res = await api.get<{ data: { userId: string; personnelNumber: string | null; noPayroll: boolean; updatedAt: string }[] }>(
    '/payroll/bmd/personnel-numbers',
  );
  return res.data.data;
}

export async function fetchBmdPreview(month: string, scope: BmdUmfang): Promise<BmdVorschau> {
  const res = await api.get<{ data: BmdVorschau }>('/payroll/bmd/preview', { params: { month, scope } });
  return res.data.data;
}

export async function createBmdHandover(month: string, scope: BmdUmfang): Promise<BmdUebergabe & { warnungen: BmdWarnung[] }> {
  const res = await api.post<{ data: BmdUebergabe & { warnungen: BmdWarnung[] } }>('/payroll/bmd/handovers', { month, scope });
  return res.data.data;
}

export async function fetchBmdHandovers(month?: string): Promise<BmdUebergabe[]> {
  const res = await api.get<{ data: BmdUebergabe[] }>('/payroll/bmd/handovers', { params: { month } });
  return res.data.data;
}

/** Die erzeugte Datei — Download und Mail liefern dieselben Bytes. */
export function bmdFileRequest(id: string): ExportRequest {
  return { url: `/payroll/bmd/handovers/${id}/export` };
}

// ── Abgabezettel lesen (AP-22 Teil 2, I) ────────────────────────────────────

/**
 * Felder einer Tageszeile, wie der Zettel sie vorschlägt, und was der Mensch daran anpasst —
 * beide aus dem Kern: Das Fenster rechnet eine Anpassung mit derselben Regel wie der Server.
 */
export type { ZettelAnpassung, ZettelFelder };

export interface ZettelVorschlagTag {
  date: string;
  felder: ZettelFelder;
  /** Felder, die unsicher gelesen wurden. */
  unsicher: string[];
  hinweise: string[];
  /** Je Hinweis das Feld, zu dem er gehört — wird es angepasst, entfällt der Hinweis. */
  hinweisFeld: string[];
  /**
   * Was der Mitarbeiter selbst gerechnet hat — nur zum Vergleich. `stundenText` sind die
   * Gesamtarbeitsstunden im Wortlaut: Aus ihnen folgt die Pause, wenn Kommt oder Geht angepasst wird.
   */
  kontrolle: { stunden: number | null; stundenText: string | null; naz: number | null; uest: number | null; za: number | null; mz: number | null; ez: number | null };
  fehlzeitWort: string | null;
  /** neu · steht schon so da · weicht von der vorhandenen Zeile ab */
  lage: 'NEW' | 'SAME' | 'DIFFERENT';
  abweichend: string[];
  /** Die Felder der vorhandenen Tageszeile (`null`, wo sie nichts trägt) — ohne Zeile `null`. */
  vorhanden: Record<string, unknown> | null;
  /** Felder, die vor dem Übernehmen angepasst wurden — auch, was der Anpassung folgt (die Pause den Zeiten). */
  angepasst: string[];
  /** Was an den angepassten Feldern gelesen worden war. */
  gelesen: Record<string, unknown>;
  /** Teil 6, V: übernommen oder verworfen — `null` heißt offen. */
  entscheidung: 'TAKEN' | 'DISCARDED' | null;
}

export interface ZettelVorschlagDaten {
  fileId: string;
  readAt: string;
  model: string | null;
  lesbar: boolean;
  grund: string | null;
  kopf: { name: string | null; monat: number | null; jahr: number | null; monatPasst: boolean; namePasst: boolean | null };
  tage: ZettelVorschlagTag[];
  /** Wie viele Tage noch zu entscheiden sind (tragen etwas, stehen nicht schon so da, nicht entschieden). */
  offen: number;
  summen: Record<string, number | null>;
  diverses: string | null;
}

export interface ZettelLesenStand {
  enabled: boolean;
  acceptedAt: string | null;
  aiAvailable: boolean;
  /** Der Hinweis zum Datenschutz, sein Kästchen und die Fassung (`lohnzettel-ki/1`) — vom Server, nicht aus der Oberfläche. */
  consentText: string;
  consentCheckbox: string;
  consentVersion: string;
}

export async function fetchSlipReading(): Promise<ZettelLesenStand> {
  const res = await api.get<{ data: ZettelLesenStand }>('/payroll/slip-reading');
  return res.data.data;
}

export async function setSlipReading(enabled: boolean, accepted?: boolean): Promise<ZettelLesenStand> {
  const res = await api.put<{ data: ZettelLesenStand }>('/payroll/slip-reading', { enabled, accepted });
  return res.data.data;
}

/** Schickt den Zettel an die KI — liefert den Vorschlag, schreibt keine Tageszeile. */
export async function readSlip(userId: string, jahr: number, monat: number, fileId: string): Promise<ZettelVorschlagDaten> {
  const res = await api.post<{ data: ZettelVorschlagDaten }>(
    `/payroll/months/${userId}/${jahr}/${monat}/files/${fileId}/read`,
    undefined,
    // Das Lesen einer Handschrift dauert — länger als die übliche Zeitgrenze.
    { timeout: 180_000 },
  );
  return res.data.data;
}

export async function fetchSlipProposal(userId: string, jahr: number, monat: number, fileId: string): Promise<ZettelVorschlagDaten> {
  const res = await api.get<{ data: ZettelVorschlagDaten }>(`/payroll/months/${userId}/${jahr}/${monat}/files/${fileId}/proposal`);
  return res.data.data;
}

export async function applySlipProposal(
  userId: string,
  jahr: number,
  monat: number,
  fileId: string,
  /** `corrections`: was am Vorschlag angepasst ist — je Tag die geänderten Felder; gilt nur für die genannten `dates`. */
  body: { dates: string[]; overwrite?: string[]; corrections?: { date: string; fields: ZettelAnpassung }[] },
  change?: string,
): Promise<{ uebernommen: string[]; ausgelassen: { date: string; grund: string }[] }> {
  const res = await api.post<{ data: { uebernommen: string[]; ausgelassen: { date: string; grund: string }[] } }>(
    `/payroll/months/${userId}/${jahr}/${monat}/files/${fileId}/apply`,
    body,
    { params: { change } },
  );
  return res.data.data;
}

/** Tage des Vorschlags verwerfen — einzeln oder alles, was noch offen ist. Schreibt keine Tageszeile. */
export async function discardSlipProposal(
  userId: string,
  jahr: number,
  monat: number,
  fileId: string,
  body: { dates: string[] } | { all: true },
): Promise<ZettelVorschlagDaten> {
  const res = await api.post<{ data: ZettelVorschlagDaten }>(`/payroll/months/${userId}/${jahr}/${monat}/files/${fileId}/discard`, body);
  return res.data.data;
}
